import { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Phone, 
  Activity,
  Server,
  Eye,
  Trash2,
  X,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Info,
  TrendingUp,
  TrendingDown,
  Shield,
  Layers,
  Clock
} from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { 
  getAllCustomers, 
  createCustomer, 
  updateCustomer, 
  deleteCustomer,
  type Customer,
  type ActivityMetric
} from '@/services/customerService';
import { CustomerDetailsSheet } from '@/components/CustomerDetailsSheet';

export function CustomersPage() {
  const { sectionColor } = useNavigation();
  
  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Fetch customers on mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Apply filters whenever search/filter changes
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchQuery, statusFilter]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    
    const response = await getAllCustomers();
    console.log('fetchCustomers response:', response);
    
    if (response.success && response.data) {
      console.log('Setting customers:', response.data.customers);
      setCustomers(response.data.customers);
    } else {
      setError(response.error || 'Failed to load customers');
    }
    
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...customers];
    console.log('applyFilters - customers:', customers);

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          (c.description && c.description.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    console.log('applyFilters - filtered:', filtered);
    setFilteredCustomers(filtered);
  };

  const handleAddCustomer = async (formData: Partial<Customer>) => {
    const customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'> = {
      name: formData.name || '',
      ...(formData.description !== undefined && { description: formData.description }),
      ...(formData.customerCount !== undefined && { customerCount: formData.customerCount }),
      ...(formData.status !== undefined && { status: formData.status }),
      ...(formData.activityMetrics !== undefined && { activityMetrics: formData.activityMetrics }),
      ...(formData.suppliers !== undefined && { suppliers: formData.suppliers }),
      ...(formData.processes !== undefined && { processes: formData.processes }),
      ...(formData.staff !== undefined && { staff: formData.staff }),
      ...(formData.systems !== undefined && { systems: formData.systems }),
    };

    const response = await createCustomer(customerData);

    if (response.success) {
      await fetchCustomers();
      setShowAddDialog(false);
    } else {
      alert(response.error || 'Failed to create customer');
    }
  };

  const handleUpdateCustomer = async (id: string, updates: Partial<Customer>) => {
    const response = await updateCustomer(id, updates);

    if (response.success) {
      await fetchCustomers();
      setEditingCustomer(null);
    } else {
      alert(response.error || 'Failed to update customer');
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteConfirm({ id, name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    const response = await deleteCustomer(deleteConfirm.id);

    if (response.success) {
      await fetchCustomers();
      setDeleteConfirm(null);
    } else {
      alert(response.error || 'Failed to delete customer');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  // Calculate stats
  const totalCustomers = customers.reduce((sum, c) => sum + (c.customerCount || 0), 0);
  const activePersonas = customers.filter((c) => c.status === 'active').length;

  // Get Themis AI Insights
  const getThemisInsights = () => {
    const insights = [];

    // Check for high churn rates
    const highChurnPersonas = customers.filter((c) => {
      const churnMetric = c.activityMetrics?.find(m => m.name === 'Churn Rate');
      return (churnMetric?.value || 0) > 5;
    });
    if (highChurnPersonas.length > 0) {
      insights.push({
        icon: AlertTriangle,
        color: 'text-red-400',
        title: `${highChurnPersonas.length} persona(s) with churn rate above 5%`,
        recommendation: 'Investigate root causes and implement retention strategies to reduce customer attrition.',
      });
    }

    // Check for low customer value
    const lowValuePersonas = customers.filter((c) => {
      const clvMetric = c.activityMetrics?.find(m => m.name === 'Customer Lifetime Value');
      const contractMetric = c.activityMetrics?.find(m => m.name === 'Average Contract Value');
      const value = clvMetric?.value || contractMetric?.value || 0;
      return value > 0 && value < 1000;
    });
    if (lowValuePersonas.length > 0) {
      insights.push({
        icon: Info,
        color: 'text-blue-400',
        title: `${lowValuePersonas.length} persona(s) with low customer value`,
        recommendation: 'Consider upselling opportunities or optimizing cost-to-serve for these segments.',
      });
    }

    // Check for inactive personas
    const inactivePersonas = customers.filter((c) => c.status === 'inactive');
    if (inactivePersonas.length > 0) {
      insights.push({
        icon: Info,
        color: 'text-neutral-400',
        title: `${inactivePersonas.length} inactive customer persona(s)`,
        recommendation: 'Archive or remove unused personas to keep your data clean.',
      });
    }

    // Check for personas without activity metrics
    const missingMetrics = customers.filter(
      (c) => !c.activityMetrics || c.activityMetrics.length === 0
    );
    if (missingMetrics.length > 0) {
      insights.push({
        icon: Info,
        color: 'text-yellow-400',
        title: `${missingMetrics.length} persona(s) missing activity metrics`,
        recommendation: 'Add activity metrics to enable better customer segment analysis and risk assessment.',
      });
    }

    // Positive insight if everything looks good
    if (insights.length === 0) {
      insights.push({
        icon: CheckCircle,
        color: 'text-green-400',
        title: 'Customer personas are well-configured',
        recommendation: 'All personas have healthy metrics and complete data for analysis.',
      });
    }

    return insights;
  };

  const themisInsights = getThemisInsights();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className={`w-8 h-8 ${sectionColor}`} />
          <div>
            <h1 className="text-3xl font-bold text-white">Customer Personas</h1>
            <p className="text-neutral-400 mt-1">
              Manage customer types and track activity metrics
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Customer Persona
        </button>
      </div>

      {/* Themis AI Insights Section */}
      <div className="bg-gradient-to-r from-primary-950/30 to-cyan-950/30 border border-primary-500/30 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-400" />
          Themis Insights
        </h3>
        <div className="space-y-3">
          {themisInsights.map((insight, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <insight.icon className={`w-5 h-5 ${insight.color} mt-0.5`} />
              <div>
                <p className="text-white font-medium">{insight.title}</p>
                <p className="text-neutral-300 text-sm">{insight.recommendation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card border border-primary-500/30 bg-primary-950/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Users className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Total Customers</p>
              <p className="text-2xl font-bold text-white">
                {totalCustomers.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="card border border-green-500/30 bg-green-950/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Active Personas</p>
              <p className="text-2xl font-bold text-white">{activePersonas}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search customer personas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input pl-10 w-full"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input pl-10 w-full appearance-none"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-neutral-400">Loading customer personas...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-950/30 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Customer Table */}
      {!loading && !error && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold">
              Customer Personas ({filteredCustomers.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-neutral-800">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">
                    Persona Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">
                    Customer Count
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">
                    Daily Activity
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">
                    Connections
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-neutral-400">
                      No customer personas found. Click "Add Customer Persona" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="border-b border-neutral-800 hover:bg-neutral-900/50"
                    >
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-white font-medium">{customer.name}</p>
                          {customer.description && (
                            <p className="text-sm text-neutral-400">
                              {customer.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-white">
                          {customer.customerCount?.toLocaleString() || '—'}
                        </p>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`badge ${
                            customer.status === 'active'
                              ? 'badge-success'
                              : customer.status === 'inactive'
                              ? 'badge-danger'
                              : 'badge-warning'
                          }`}
                        >
                          {customer.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1 text-sm">
                          {customer.activityMetrics && customer.activityMetrics.length > 0 ? (
                            customer.activityMetrics.map((metric, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-neutral-300">
                                {metric.icon === 'TrendingUp' && <TrendingUp className="w-3 h-3" />}
                                {metric.icon === 'TrendingDown' && <TrendingDown className="w-3 h-3" />}
                                {metric.icon === 'Users' && <Users className="w-3 h-3" />}
                                {metric.icon === 'Sparkles' && <Sparkles className="w-3 h-3" />}
                                {metric.icon === 'CheckCircle' && <CheckCircle className="w-3 h-3" />}
                                {metric.icon === 'Shield' && <Shield className="w-3 h-3" />}
                                {metric.icon === 'Layers' && <Layers className="w-3 h-3" />}
                                {metric.icon === 'Clock' && <Clock className="w-3 h-3" />}
                                {metric.icon === 'Phone' && <Phone className="w-3 h-3" />}
                                {metric.icon === 'Activity' && <Activity className="w-3 h-3" />}
                                {metric.icon === 'Server' && <Server className="w-3 h-3" />}
                                {metric.icon === 'AlertTriangle' && <AlertTriangle className="w-3 h-3" />}
                                {!metric.icon && <Activity className="w-3 h-3" />}
                                <span>{metric.value.toLocaleString()} {metric.name.toLowerCase()}</span>
                                {metric.unit && <span className="text-neutral-500 text-xs">({metric.unit})</span>}
                              </div>
                            ))
                          ) : (
                            <span className="text-neutral-500">No metrics</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-wrap gap-2">
                          {customer.processes && customer.processes.length > 0 && (
                            <span className="badge badge-primary">
                              {customer.processes.length} processes
                            </span>
                          )}
                          {customer.suppliers && customer.suppliers.length > 0 && (
                            <span className="badge badge-primary">
                              {customer.suppliers.length} suppliers
                            </span>
                          )}
                          {customer.staff && customer.staff.length > 0 && (
                            <span className="badge badge-primary">
                              {customer.staff.length} staff
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setIsDetailsOpen(true);
                            }}
                            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-primary-400" />
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteClick(customer.id, customer.name)
                            }
                            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {(showAddDialog || editingCustomer) && (
        <CustomerDialog
          customer={editingCustomer}
          onClose={() => {
            setShowAddDialog(false);
            setEditingCustomer(null);
          }}
          onSave={(data) => {
            if (editingCustomer) {
              handleUpdateCustomer(editingCustomer.id, data);
            } else {
              handleAddCustomer(data);
            }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-neutral-900 rounded-lg shadow-xl max-w-md w-full mx-4 border border-neutral-800">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-950/30 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
                  <p className="text-sm text-neutral-400">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-neutral-300 mb-6">
                Are you sure you want to delete <span className="font-semibold text-white">"{deleteConfirm.name}"</span>?
              </p>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Delete Persona
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Sheet */}
      <CustomerDetailsSheet
        customer={selectedCustomer}
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedCustomer(null);
        }}
        onUpdate={fetchCustomers}
      />
    </div>
  );
}

// Customer Add/Edit Dialog Component
interface CustomerDialogProps {
  customer: Customer | null;
  onClose: () => void;
  onSave: (data: Partial<Customer>) => void;
}

function CustomerDialog({ customer, onClose, onSave }: CustomerDialogProps) {
  const [formData, setFormData] = useState<Partial<Customer>>(
    customer || {
      name: '',
      description: '',
      customerCount: 0,
      status: 'active',
      activityMetrics: [],
    }
  );
  
  // Helper to add a new metric
  const addMetric = () => {
    const newMetrics = [...(formData.activityMetrics || [])];
    newMetrics.push({ name: '', value: 0, unit: 'per day', icon: 'Activity' });
    setFormData({ ...formData, activityMetrics: newMetrics });
  };
  
  // Helper to update a metric
  const updateMetric = (index: number, field: keyof ActivityMetric, value: string | number) => {
    const newMetrics = [...(formData.activityMetrics || [])];
    const currentMetric = newMetrics[index];
    
    if (!currentMetric) return;
    
    // Ensure all required fields are present
    newMetrics[index] = {
      name: currentMetric.name,
      value: currentMetric.value,
      unit: currentMetric.unit,
      icon: currentMetric.icon,
      [field]: value
    } as ActivityMetric;
    
    setFormData({ ...formData, activityMetrics: newMetrics });
  };
  
  // Helper to remove a metric
  const removeMetric = (index: number) => {
    const newMetrics = [...(formData.activityMetrics || [])];
    newMetrics.splice(index, 1);
    setFormData({ ...formData, activityMetrics: newMetrics });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 rounded-lg border border-neutral-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Dialog Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <h2 className="text-xl font-semibold text-white">
            {customer ? 'Edit Customer Persona' : 'Add Customer Persona'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        {/* Dialog Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Basic Information</h3>
            
            <div>
              <label className="form-label">
                Persona Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="form-input w-full"
                placeholder="e.g., D2C Customers, Enterprise Clients"
              />
            </div>

            <div>
              <label className="form-label">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="form-input w-full"
                rows={3}
                placeholder="Describe this customer segment..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Customer Count</label>
                <input
                  type="number"
                  min="0"
                  value={formData.customerCount || 0}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      customerCount: parseInt(e.target.value) || 0,
                    })
                  }
                  className="form-input w-full"
                  placeholder="Number of customers"
                />
              </div>

              <div>
                <label className="form-label">Status</label>
                <select
                  value={formData.status || 'active'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as 'active' | 'inactive' | 'pending',
                    })
                  }
                  className="form-input w-full"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Activity Metrics */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Daily Activity Metrics
              </h3>
              <button
                type="button"
                onClick={addMetric}
                className="btn-outline text-sm py-1 px-3"
              >
                + Add Metric
              </button>
            </div>
            
            {formData.activityMetrics && formData.activityMetrics.length > 0 ? (
              <div className="space-y-3">
                {formData.activityMetrics.map((metric, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-4">
                      <label className="form-label text-xs">Metric Name</label>
                      <input
                        type="text"
                        value={metric.name}
                        onChange={(e) => updateMetric(index, 'name', e.target.value)}
                        className="form-input w-full text-sm"
                        placeholder="e.g., Payments"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="form-label text-xs">Volume</label>
                      <input
                        type="number"
                        min="0"
                        value={metric.value}
                        onChange={(e) => updateMetric(index, 'value', parseInt(e.target.value) || 0)}
                        className="form-input w-full text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="form-label text-xs">Unit</label>
                      <input
                        type="text"
                        value={metric.unit || ''}
                        onChange={(e) => updateMetric(index, 'unit', e.target.value)}
                        className="form-input w-full text-sm"
                        placeholder="/day"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="form-label text-xs">Icon</label>
                      <input
                        type="text"
                        value={metric.icon || ''}
                        onChange={(e) => updateMetric(index, 'icon', e.target.value)}
                        className="form-input w-full text-sm"
                        placeholder="📊"
                      />
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeMetric(index)}
                        className="w-full p-2 text-red-400 hover:text-red-300 hover:bg-red-950/30 rounded transition-colors"
                        title="Remove metric"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-neutral-400 text-sm italic">
                No metrics added yet. Click "Add Metric" to start tracking customer activity.
              </p>
            )}
          </div>

          {/* Dialog Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-800">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {customer ? 'Update Persona' : 'Create Persona'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
